import { ReactNode, useMemo, useState } from 'react';
import { Box, MenuItem, Stack, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BudgetScenarioType, DATE_FORMATS } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import BucketVersionFields from './BucketVersionFields';
import ScenarioFormRow from './ScenarioFormRow';
import { SCENARIO_TYPES, ScenarioMetaValue, scenarioDateErrors, scenarioNameError } from './scenarioMeta';
import dayjs from '@utils/dayjs';

/** The two period fields, as the per-field touched/invalid flags key them. */
type DateKey = 'startDate' | 'endDate';

interface ScenarioMetaFormProps {
    value: ScenarioMetaValue;
    onChange: (value: ScenarioMetaValue) => void;
    // 'create' is the only mode that may still choose the type: switching it afterwards would strand
    // the previous type's input rows and flip the consolidated budget-vs-cost classification.
    mode: 'create' | 'edit';
    // Read-only role, or a save in flight.
    disabled?: boolean;
    // Type-specific scope fields (Worktypes / Partner / Location …). Empty for now — the per-type
    // pickers still live inside each type's input grid, where the rows they create are the single
    // source of truth. Wiring them up here is one follow-up per type.
    scopeSlot?: ReactNode;
}

// The board's "Neues Szenario anlegen" form: Name and Beschreibung side by side, then the budget
// (bucket + version), the scenario type on a line of its own, and the Start/Ende pair. Shared by the
// create page and the scenario page's header, so a scenario is edited through the fields it was
// created with. Laid out as explicit rows rather than one wrapping grid, so a field the board puts on
// its own line stays there instead of drifting up next to its neighbour.
const ScenarioMetaForm = ({ value, onChange, mode, disabled = false, scopeSlot }: ScenarioMetaFormProps) => {
    const { t } = useTranslation('budget');

    // "Titel ist erforderlich" used to be red on the create page's FIRST paint, while Bucket, Start, Ende and
    // Art — equally required and equally empty — said nothing (CALL-784). Save is disabled either way, so the
    // message is only useful once the user has actually left the field empty behind them.
    const [nameTouched, setNameTouched] = useState(false);
    const nameError = useMemo(() => scenarioNameError(value.name, t), [value.name, t]);
    const dateErrors = useMemo(
        () => scenarioDateErrors(value.startDate, value.endDate, t),
        [value.startDate, value.endDate, t]
    );
    const shownNameError = nameTouched ? nameError : null;

    // Same rule as the title, per date field: an empty required field is only worth a message once the user
    // has left it that way — by clearing it, half-typing it, or tabbing past it. Both modes start untouched,
    // including the detail page: a persisted period can never BE empty (both columns are NOT NULL), and this
    // form renders for one frame with the empty default before the fetched scenario is adopted, which
    // pre-touching would paint red.
    const [touched, setTouched] = useState<Record<DateKey, boolean>>({ startDate: false, endDate: false });
    // A date the picker could not parse ("15.09.0026" while the year is still being typed, or an impossible
    // one) is reported as an 'invalidDate' validation error alongside a value the draft cannot hold. The
    // value is dropped, so this flag is the only thing that can tell the two states apart afterwards —
    // without it a field still showing "DD.MM.0026" would be described as merely empty (CALL-784).
    const [invalid, setInvalid] = useState<Record<DateKey, boolean>>({ startDate: false, endDate: false });

    const set = <K extends keyof ScenarioMetaValue>(key: K, next: ScenarioMetaValue[K]) =>
        onChange({ ...value, [key]: next });

    // The period is stored as a DATEONLY 'YYYY-MM-DD' string, so it is converted at the field rather than
    // held as a dayjs: an invalid or half-typed date reports null and clears it — but it no longer does so
    // silently, see `invalid` above.
    const setDate = (key: DateKey, next: dayjs.Dayjs | null, isInvalid: boolean) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
        setInvalid((prev) => ({ ...prev, [key]: isInvalid }));
        set(key, next && next.isValid() ? next.format(DATE_FORMATS.ISO_DATE) : '');
    };
    const dateValue = (raw: string) => (raw ? dayjs(raw, DATE_FORMATS.ISO_DATE) : null);

    // Red outline, aria-invalid and helper text, from whichever of the two states the field is in.
    //
    // The touched gate covers the EMPTY case only, which is all it was ever meant to hold back: "Datum ist
    // erforderlich" on a field nobody has been in yet. A message about a date that IS on screen is never
    // premature — and suppressing it hid the range errors completely (CALL-794), because those are reported
    // on Ende while the user may have inverted the period by moving START past it. Ende was then untouched,
    // so its own flag silenced the only field carrying the message: SPEICHERN grey, the footer asking for
    // the marked entries to be corrected, and nothing on the page marked.
    const dateError = (key: DateKey): string | null => {
        if (invalid[key]) return t('validation.invalidDate');
        return touched[key] || value[key] ? dateErrors[key] : null;
    };

    // Everything a period field needs beyond its own value, so Start and Ende cannot drift apart.
    const dateFieldProps = (key: DateKey) => {
        const error = dateError(key);
        return {
            textField: {
                fullWidth: true,
                size: 'small' as const,
                required: true,
                error: Boolean(error),
                // Wrapped: the message appears, changes ("erforderlich" → "ungültig") and disappears as the
                // user types, and a translation extension rewriting a bare text node React is about to update
                // is what crashes reconciliation (frontend/CLAUDE.md).
                helperText: error ? <span>{error}</span> : undefined,
                onBlur: () => setTouched((prev) => ({ ...prev, [key]: true })),
            },
        };
    };

    return (
        <Stack spacing={2}>
            <ScenarioFormRow>
                <Box>
                    <TextField
                        fullWidth
                        label={t('field.scenarioName')}
                        value={value.name}
                        onChange={(e) => set('name', e.target.value)}
                        onBlur={() => setNameTouched(true)}
                        size="small"
                        required
                        disabled={disabled}
                        error={Boolean(shownNameError)}
                        helperText={shownNameError ?? undefined}
                        inputProps={{ className: 'notranslate' }}
                    />
                </Box>
                <Box>
                    <TextField
                        fullWidth
                        label={t('field.description')}
                        value={value.description}
                        onChange={(e) => set('description', e.target.value)}
                        size="small"
                        multiline
                        minRows={1}
                        disabled={disabled}
                        inputProps={{ className: 'notranslate' }}
                    />
                </Box>
            </ScenarioFormRow>

            <BucketVersionFields
                versionId={value.budgetVersionId}
                onChange={(versionId) => set('budgetVersionId', versionId)}
                // A scenario cannot leave its bucket once it has input rows keyed to that bucket's
                // worktypes and locations.
                lockBucket={mode === 'edit'}
                disabled={disabled}
            />

            {/* MUI date pickers rather than `<input type="date">`: a native date input renders in the
                BROWSER's locale, so the period read "2026-08-15" while the grids, the scenario list and every
                other date in the app read "15.08.2026" (CALL-784). The adapter locale is pinned to 'de' for
                the same reason the budget grids pin their number formatters — the format must not depend on
                the machine the page happens to run on. */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de" dateLibInstance={dayjs}>
                <ScenarioFormRow>
                    <Box>
                        <DatePicker
                            label={t('field.startDate')}
                            format={DATE_FORMATS.DATE}
                            value={dateValue(value.startDate)}
                            onChange={(next, context) =>
                                setDate('startDate', next, context?.validationError === 'invalidDate')
                            }
                            disabled={disabled}
                            slotProps={dateFieldProps('startDate')}
                        />
                    </Box>
                    <Box>
                        <DatePicker
                            label={t('field.endDate')}
                            format={DATE_FORMATS.DATE}
                            value={dateValue(value.endDate)}
                            onChange={(next, context) =>
                                setDate('endDate', next, context?.validationError === 'invalidDate')
                            }
                            disabled={disabled}
                            slotProps={dateFieldProps('endDate')}
                        />
                    </Box>
                </ScenarioFormRow>
            </LocalizationProvider>

            {/* Deliberately AFTER the period, unlike the board: the type decides which input editor
                follows this form, so it reads better right next to the type-specific fields. */}
            <ScenarioFormRow>
                <Box>
                    <TextField
                        select
                        fullWidth
                        label={t('field.scenarioType')}
                        value={value.type}
                        onChange={(e) => set('type', e.target.value as BudgetScenarioType)}
                        size="small"
                        required
                        disabled={disabled || mode === 'edit'}
                        helperText={mode === 'edit' ? t('hint.scenarioTypeLocked') : undefined}
                    >
                        {SCENARIO_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                                {t(`scenarioType.${type}`)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
            </ScenarioFormRow>

            {scopeSlot}
        </Stack>
    );
};

export default ScenarioMetaForm;
